import { useCallback, useEffect, useRef, useState } from 'react';
import useBackendApi from './useBackendApi';

type DownloadGuideOptions = {
  autoFetch?: boolean;
};

const getDownloadErrorMessage = (status: number): string => {
  if (status === 404) {
    return 'This PDF is not available.';
  }
  return 'Unable to download the PDF right now. Please try again.';
};

const useDownloadGuide = (url: string, guideName: string, options?: DownloadGuideOptions) => {
  const { apiFetchRequest } = useBackendApi();
  const autoFetch = options?.autoFetch !== false;
  const [userGuideData, setUserGuideData] = useState<Blob | null>(null);
  const [userGuideName, setUserGuideName] = useState<string>(guideName);
  const [error, setError] = useState<string>('');
  const [isGuideDownloading, setIsGuideDownloading] = useState<boolean>(autoFetch);
  const userGuideDataRef = useRef<Blob | null>(null);
  const userGuideNameRef = useRef<string>(guideName);
  const lastFetchedUrlRef = useRef<string | null>(null);

  useEffect(() => {
    userGuideDataRef.current = userGuideData;
  }, [userGuideData]);

  useEffect(() => {
    userGuideNameRef.current = userGuideName;
  }, [userGuideName]);

  useEffect(() => {
    setError('');
    if (!autoFetch) {
      setIsGuideDownloading(false);
    }
  }, [url, autoFetch]);

  const extractFileName = useCallback(
    (contentDisposition: string | null) => {
      let fileName = guideName;

      if (!contentDisposition) {
        return fileName;
      }

      const utf8Match = contentDisposition.match(/filename\*=utf-8''([^;]+)/i);
      if (utf8Match?.[1]) {
        return decodeURIComponent(utf8Match[1]);
      }

      const quotedMatch = contentDisposition.match(/filename=\"([^\"]+)\"/i);
      if (quotedMatch?.[1]) {
        return quotedMatch[1];
      }

      const plainMatch = contentDisposition.match(/filename=([^;]+)/i);
      if (plainMatch?.[1]) {
        return plainMatch[1].trim();
      }

      return fileName;
    },
    [guideName],
  );

  const fetchGuide = useCallback(async () => {
    if (!url) {
      setError('User guide not available for download.');
      setIsGuideDownloading(false);
      return null;
    }

    setIsGuideDownloading(true);
    setError('');

    try {
      const data = await apiFetchRequest({
        endpoint: url,
        isFile: true,
      });

      if (!data) {
        setError('Failed to fetch file. Please try again.');
        setIsGuideDownloading(false);
        return null;
      }

      const { status, body, headers, isError } = data;
      if (status === 401) {
        setIsGuideDownloading(false);
        window.location.href = '/401.html';
        return null;
      }

      if (isError || status >= 400 || !(body instanceof Blob)) {
        setError(getDownloadErrorMessage(status));
        setIsGuideDownloading(false);
        return null;
      }

      const contentDisposition = headers?.get('content-disposition') || headers?.get('Content-Disposition');
      const fileName = extractFileName(contentDisposition);

      setUserGuideData(body);
      setUserGuideName(fileName);
      lastFetchedUrlRef.current = url;
      setIsGuideDownloading(false);
      return { blob: body, filename: fileName };
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : 'An unknown error occurred');
      console.log('Error fetching user guide:', exception);
      setIsGuideDownloading(false);
      return null;
    }
  }, [apiFetchRequest, extractFileName, url]);

  useEffect(() => {
    if (autoFetch) {
      void fetchGuide();
    }
  }, [autoFetch, fetchGuide]);

  const downloadBlob = useCallback((blob: Blob, fileName: string) => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  }, []);

  const handleDownloadClick = useCallback(async () => {
    if (userGuideDataRef.current && lastFetchedUrlRef.current === url) {
      downloadBlob(userGuideDataRef.current, userGuideNameRef.current || guideName);
      return;
    }

    const fetched = await fetchGuide();
    if (fetched?.blob) {
      downloadBlob(fetched.blob, fetched.filename || guideName);
      return;
    }
  }, [downloadBlob, fetchGuide, guideName, url]);

  return { handleDownloadClick, isGuideDownloading, error };
};

export default useDownloadGuide;
